import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getService, getServices } from '@/lib/api';
import { motion } from 'framer-motion';
import { fadeUp, stagger } from '@/lib/motion';
import { PageHero, Section, SectionTitle } from '@/components/shared/Section';
import { EnterpriseForm } from '@/components/shared/Forms';
import TCOCalculator from '@/components/shared/TCOCalculator';
import PilotBanner from '@/components/shared/PilotBanner';
import StickyMiniForm from '@/components/shared/StickyMiniForm';
import ProjectGallery from '@/components/shared/ProjectGallery';
import { ArrowRight, ShieldCheck, CheckCircle, MessageCircle, Rocket, Clock, Star, Zap } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const serviceFAQs = {
  'piloto': [
    { q: '¿Qué incluye el piloto?', a: 'Equipos reales, instalación, configuración, integración básica con tu sistema, soporte dedicado y un reporte ejecutivo de ROI al finalizar.' },
    { q: '¿Cuánto dura?', a: '30 días calendario, con posibilidad de extensión según evaluación mutua.' },
    { q: '¿Tiene costo?', a: 'El piloto tiene un costo reducido que se descuenta del proyecto si se aprueba. Consulta condiciones específicas.' },
    { q: '¿Qué pasa al terminar?', a: 'Recibes un reporte de ROI. Puedes comprar, arrendar o devolver los equipos sin compromiso adicional.' },
  ],
  'arriendo-leasing': [
    { q: '¿Cuál es el plazo mínimo?', a: 'El plazo mínimo de arriendo es de 12 meses, con opciones de 24 y 36 meses con mejores condiciones.' },
    { q: '¿Qué incluye la cuota?', a: 'Hardware, software básico, soporte técnico, seguro de equipos y renovación tecnológica según plan.' },
    { q: '¿Sirve para licitaciones?', a: 'Sí, el modelo OPEX es ideal para sector público ya que no requiere inversión de capital.' },
    { q: '¿Puedo escalar?', a: 'Sí, puedes agregar o reducir equipos durante el contrato según tus necesidades operacionales.' },
  ],
  'default': [
    { q: '¿Cómo funciona el SLA?', a: 'Definimos tiempos de respuesta y resolución según criticidad. Desde 2 horas para emergencias hasta 24 horas para incidencias menores.' },
    { q: '¿Tienen cobertura en regiones?', a: 'Sí, cubrimos las principales ciudades de Chile con técnicos en terreno y red de aliados certificados.' },
    { q: '¿Qué pasa si un equipo falla?', a: 'Activamos reemplazo desde nuestro stock de repuestos. En muchos casos, resolvemos remotamente en minutos.' },
  ]
};

export default function ServicePage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getService(slug).catch(() => null),
      getServices().catch(() => ({ data: [] }))
    ]).then(([s, all]) => {
      setService(s?.data || null);
      setAllServices(all?.data || []);
      setLoading(false);
    });
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0EA5E9] border-t-transparent rounded-full animate-spin" /></div>;
  if (!service) return <div className="min-h-screen flex items-center justify-center text-slate-500">Servicio no encontrado</div>;

  const faqs = serviceFAQs[slug] || serviceFAQs['default'];

  return (
    <div>
      <PageHero
        badge={service.badge}
        badgeIcon={ShieldCheck}
        h1={service.h1}
        subtitle={service.subtitle}
        primaryCTA={<Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg transition-all">{slug === 'piloto' ? 'Solicitar mi piloto' : 'Cotizar'} <ArrowRight className="w-4 h-4" /></Link>}
        secondaryCTA={<a href="https://wa.me/56975294340" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-emerald-400 border border-emerald-400/30 rounded-xl hover:bg-emerald-400/10 transition-colors"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
      />

      {/* Features */}
      <Section>
        <SectionTitle badge="QUÉ INCLUYE" title={`Todo sobre ${service.title}`} subtitle={service.description} />
        <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {service.features?.map((f, i) => (
            <motion.div key={i} variants={fadeUp} className="flex items-start gap-3 p-5 rounded-xl bg-white border border-slate-100 shadow-sm">
              <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
              <span className="text-sm text-slate-700">{f}</span>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* Pilot Process (for piloto page) */}
      {slug === 'piloto' && (
        <Section alt>
          <SectionTitle badge="PROCESO" title="4 pasos para tu piloto" center />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Star, title: 'Evaluación', desc: 'Analizamos tu operación y definimos alcance del piloto.' },
              { icon: Zap, title: 'Instalación', desc: 'Instalamos equipos reales en tu local en 5-7 días.' },
              { icon: Clock, title: '30 días', desc: 'Operación real con soporte dedicado y monitoreo.' },
              { icon: Rocket, title: 'Reporte ROI', desc: 'Entregamos reporte ejecutivo con métricas y recomendaciones.' },
            ].map((step, i) => (
              <div key={i} className="text-center p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-sky-50 flex items-center justify-center">
                  <step.icon className="w-6 h-6 text-[#0EA5E9]" />
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-[#0EA5E9] mb-2">Paso {i + 1}</div>
                <h4 className="text-base font-semibold mb-2" style={{fontFamily:'Syne,sans-serif'}}>{step.title}</h4>
                <p className="text-sm text-[#64748B]">{step.desc}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* CAPEX vs OPEX for arriendo page */}
      {slug === 'arriendo-leasing' && (
        <Section alt>
          <SectionTitle badge="COMPARADOR" title="CAPEX vs OPEX" subtitle="Compara los modelos de adquisición y elige el mejor para tu empresa." center />
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-3 gap-0 rounded-2xl overflow-hidden border border-slate-200">
              <div className="bg-slate-50 p-4 font-semibold text-sm border-b border-slate-200">Aspecto</div>
              <div className="bg-slate-50 p-4 font-semibold text-sm text-center border-b border-l border-slate-200">CAPEX (Compra)</div>
              <div className="bg-[#0EA5E9]/5 p-4 font-semibold text-sm text-center border-b border-l border-slate-200 text-[#0EA5E9]">OPEX (Arriendo)</div>
              {[
                ['Inversión inicial', 'Alta', 'Cero'],
                ['Depreciación', 'Sí', 'No aplica'],
                ['Soporte incluido', 'Opcional', 'Incluido'],
                ['Renovación tech', 'Manual', 'Automática'],
                ['Escalabilidad', 'Limitada', 'Flexible'],
                ['Ideal para', 'Pequeños volúmenes', 'Enterprise / Sector público'],
              ].map(([aspect, capex, opex], i) => (
                <React.Fragment key={i}>
                  <div className="p-4 text-sm text-slate-700 border-b border-slate-100">{aspect}</div>
                  <div className="p-4 text-sm text-center text-slate-600 border-b border-l border-slate-100">{capex}</div>
                  <div className="p-4 text-sm text-center font-medium text-[#0A2540] border-b border-l border-slate-100 bg-[#0EA5E9]/5">{opex}</div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* TCO Calculator for arriendo-leasing */}
      {slug === 'arriendo-leasing' && (
        <Section>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <SectionTitle badge="CALCULA TU TCO" title="¿Cuánto ahorras con OPEX?" subtitle="Compara el costo total de propiedad entre comprar y arrendar. Incluye soporte, depreciación y renovación." />
              <div className="space-y-3 text-sm text-[#64748B]">
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" /> Cero inversión inicial (CAPEX)</div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" /> Soporte y mantenimiento incluido</div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" /> Renovación tecnológica automática</div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" /> Ideal para licitaciones públicas</div>
              </div>
            </div>
            <TCOCalculator sourcePage={`/servicios/${slug}`} />
          </div>
        </Section>
      )}

      {/* Project Gallery */}
      <ProjectGallery context="default" title={`Proyectos de ${service?.title || 'este servicio'}`} />

      {/* Pilot Banner on non-pilot service pages */}
      {slug !== 'piloto' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <PilotBanner />
        </div>
      )}

      {/* FAQ */}
      <Section>
        <SectionTitle badge="FAQ" title="Preguntas frecuentes" center />
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl px-5 data-[state=open]:shadow-sm">
                <AccordionTrigger className="text-sm font-medium text-left py-4 hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-[#64748B] pb-4 leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* Form */}
      <Section alt>
        <div className="max-w-3xl mx-auto">
          <SectionTitle badge="CONTACTO" title={slug === 'piloto' ? 'Solicita tu piloto' : 'Cotiza este servicio'} center />
          <div className="p-6 sm:p-8 rounded-2xl bg-white border border-slate-200 shadow-lg">
            <EnterpriseForm sourcePage={`/servicios/${slug}`} />
          </div>
        </div>
      </Section>

      {/* Other Services */}
      {allServices.length > 1 && (
        <Section>
          <SectionTitle badge="MÁS SERVICIOS" title="Otros servicios" center />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {allServices.filter(s => s.slug !== slug).slice(0, 3).map((s) => (
              <Link key={s.slug} to={`/servicios/${s.slug}`} className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
                <h4 className="text-sm font-semibold group-hover:text-[#0EA5E9] transition-colors">{s.title}</h4>
                <p className="text-xs text-[#64748B] mt-1">{s.description}</p>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Sticky mini-form */}
      <StickyMiniForm sourcePage={`/servicios/${slug}`} />
    </div>
  );
}
